import {observer} from 'mobx-react';
import React from 'react';
import {TimeZone} from '../../domain';
import {PageHeading, PromiseMessage, PromiseRetry} from '../../lib/components';
import {ReactNodeGenerator} from '../../lib/helpers';
import {AddTimeZoneControls} from './parts/AddTimeZoneControls';
import {AddTimeZoneForm} from './parts/AddTimeZoneForm';
import {DeleteTimeZoneControls} from './parts/DeleteTimeZoneControls';
import {EditTimeZoneControls} from './parts/EditTimeZoneControls';
import {EditTimeZoneForm} from './parts/EditTimeZoneForm';
import {TimeZoneComponentStore} from './TimeZoneComponentStore';

function TimeZoneViewNoActions(timeZone: TimeZone): React.ReactNode {
  return (
    <div className="row" key={'timezone-' + timeZone.Id}>
      <div className="col">
        <div className="text-box">
          {timeZone.UtcName}
        </div>
      </div>
      <div className="col">
        <div className="text-box">
          {timeZone.UtcOffsetMinutes}
        </div>
      </div>
    </div>
  );
}

function* ViewModeItems(store: TimeZoneComponentStore): ReactNodeGenerator {
  const timeZones = store.timeZones;

  yield (
    <div className="row justify-content-end mb-1" key="view-actions">
      <div className="col-auto no-wrap">
        <button type="button" className="btn btn-primary"
                onClick={store.beginAdd}> Add
        </button>
      </div>
    </div>);

  if (timeZones.length === 0)
    yield (
      <div className="row justify-content-center" key="no-timezones">
        <div className="col-auto">
          <div className="alert alert-info">
            No Time Zones
          </div>
        </div>
      </div>);

  for (const timeZone of timeZones) {
    yield (
      <div className="row" key={'timezone-' + timeZone.Id}>
        <div className="col">
          <div className="text-box">
            {timeZone.UtcName}
          </div>
        </div>
        <div className="col">
          <div className="text-box">
            {timeZone.UtcOffsetMinutes}
          </div>
        </div>
        <div className="col-auto no-wrap">
          <button type="button" className="btn btn-primary"
                  onClick={() => store.beginEdit(timeZone)}> Edit
          </button>
          <button type="button" className="btn btn-danger"
                  onClick={() => store.beginDelete(timeZone)}> Delete
          </button>
        </div>
      </div>);
  }
}

function* AddModeItems(store: TimeZoneComponentStore): ReactNodeGenerator {
  yield (<AddTimeZoneForm key="timezone-add-form"
                          store={store}/>);

  yield (<AddTimeZoneControls key="timezone-add-controls"
                              store={store}/>);

  for (const timeZone of store.timeZones)
    yield TimeZoneViewNoActions(timeZone);
}

function* EditModeItems(store: TimeZoneComponentStore): ReactNodeGenerator {
  const editTimeZoneId = store.editTimeZone!.timeZone.Id;

  for (const timeZone of store.timeZones) {
    if (timeZone.Id !== editTimeZoneId) {
      yield TimeZoneViewNoActions(timeZone);
      continue;
    }

    yield (<EditTimeZoneForm key="edit-timezone-form"
                             store={store}/>);

    yield (<EditTimeZoneControls key="edit-timezone-controls"
                                 store={store}/>);
  }
}

function* DeleteModeItems(store: TimeZoneComponentStore): ReactNodeGenerator {
  const deleteTimeZoneId = store.deleteTimeZone!.timeZone.Id;

  for (let timeZone of store.timeZones) {
    yield TimeZoneViewNoActions(timeZone);

    if (timeZone.Id !== deleteTimeZoneId) {
      continue;
    }

    yield (<DeleteTimeZoneControls key="delete-time-zone-controls"
                                   store={store}/>);
  }
}

function* cardBodyRenderer(store: TimeZoneComponentStore): ReactNodeGenerator {
  const mode = store.mode;

  switch (mode) {
    case 'view':
      yield* ViewModeItems(store);
      break;
    case 'edit':
      yield* EditModeItems(store);
      break;
    case 'add':
      yield* AddModeItems(store);
      break;
    case 'delete':
      yield* DeleteModeItems(store);
      break;
  }
}

function* sceneRenderer(store: TimeZoneComponentStore): ReactNodeGenerator {
  const timeZonesState = store.timeZonesState;

  yield (<PageHeading key="heading">Time Zones</PageHeading>);

  yield (<PromiseMessage key="time-zone-loader"
                         label="Loading time zones" align="center"
                         state={timeZonesState}/>);

  yield (<PromiseRetry key="time-zone-retry"
                       state={timeZonesState}
                       retry={store.loadTimeZones} align="center"/>);

  if (timeZonesState?.isResolved !== true)
    return;

  yield (
    <div className="row" key="main">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            Time Zones
          </div>

          <div className="card-body">
            {Array.from(cardBodyRenderer(store))}
          </div>
        </div>
      </div>
    </div>
  );
}

@observer
export class TimeZoneComponent extends React.Component {
  store: TimeZoneComponentStore;

  constructor(props: any) {
    super(props);
    this.store = new TimeZoneComponentStore();
  }

  componentDidMount() {
    this.store.loadTimeZones();
  }

  render() {
    return Array.from(sceneRenderer(this.store));
  }

  componentWillUnmount() {
    this.store.dispose();
  }
}
